import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InfoTip } from "@/components/InfoTip";
import { Loader2, Link2, Copy, RefreshCw, CheckCircle, AlertTriangle, XCircle, Banknote, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_LABELS = {
  link_created: { label: "Invite sent — awaiting customer", cls: "bg-amber-100 text-amber-700" },
  mandate_active: { label: "Customer subscribed ✓", cls: "bg-emerald-100 text-emerald-700" },
  cycle_active: { label: "Payment cycle live ✓", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-600" },
  mandate_cancelled: { label: "Mandate cancelled", cls: "bg-red-100 text-red-700" },
  mandate_failed: { label: "Mandate failed", cls: "bg-red-100 text-red-700" },
};

const HOW_TO = {
  dd: `How Direct Debit setup works:

1. Create Subscribe Link — generates a secure GoCardless page for this customer.
2. Send the link to the customer (copy it into an email/SMS).
3. Customer enters their bank details and authorises the Bacs Direct Debit mandate.
4. Status updates automatically (webhook) or via "Check Status".
5. Once subscribed, set the payment cycle — amount, frequency and start date are pulled from the loan. Daily loans become 5 weekly subscriptions (Mon–Fri).`,
  vrp: `How VRP setup works:

1. Create Subscribe Link — generates a GoCardless page for this customer.
2. Customer authorises a Variable Recurring Payment mandate via their bank (Faster Payments).
3. Once authorised, payments are collected against the mandate within the agreed limits.

Note: requires commercial VRP enablement on our GoCardless account.`,
};

// Mini progress stepper: invite -> subscribed -> cycle live
const GCStepper = ({ status }) => {
  const stepIdx = status === "cycle_active" ? 3 : status === "mandate_active" ? 2 : status === "link_created" ? 1 : 0;
  const steps = ["Invite sent", "Customer subscribed", "Payment cycle live"];
  return (
    <div className="flex items-center" data-testid="gc-stepper">
      {steps.map((s, i) => (
        <div key={s} className={`flex items-center ${i < steps.length - 1 ? "flex-1" : ""}`}>
          <div className="flex flex-col items-center">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
              i < stepIdx ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
            }`}>
              {i < stepIdx ? "✓" : i + 1}
            </div>
            <span className="text-[9px] text-muted-foreground mt-0.5 whitespace-nowrap">{s}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`h-0.5 flex-1 mx-1 mb-3 ${i < stepIdx - 1 ? "bg-emerald-400" : "bg-slate-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
};

// GoCardless DD/VRP collection setup — Stage 3 tabs (marked NOT IN USE while
// Standing Order remains the live method; fully functional for testing).
export const GCCollectionPanel = ({ application, method, onRefresh }) => {
  const appId = application.application_id || application.id;
  const gc = application.gc_collection;
  const isThisMethod = gc && gc.method === method && gc.status !== "cancelled";
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const [cycle, setCycle] = useState({
    amount_per_payment: application.payment_amount || application.nominal_payment || "",
    frequency: (application.payment_frequency || "weekly").toLowerCase().includes("dai") ? "daily"
      : (application.payment_frequency || "weekly").toLowerCase().includes("month") ? "monthly" : "weekly",
    start_date: "",
    num_payments: parseInt(String(application.loan_term || "").replace(/\D/g, "")) || "",
  });

  const checkStatus = useCallback(async (silent = false) => {
    try {
      if (!silent) setBusy("status");
      const { data } = await api.get(`/gc/applications/${appId}/status`);
      setStatus(data.gc_collection);
      if (data.gc_collection?.status === "mandate_active" && gc?.status === "link_created") {
        toast.success("Customer has authorised the mandate!");
        onRefresh?.();
      }
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Failed to check GoCardless status");
    } finally {
      setBusy(null);
    }
  }, [appId, gc?.status, onRefresh]);

  useEffect(() => {
    if (isThisMethod) checkStatus(true);
  }, [isThisMethod, checkStatus]);

  const createLink = async () => {
    try {
      setBusy("create");
      await api.post(`/gc/applications/${appId}/billing-request`, {
        method,
        amount_per_payment: parseFloat(cycle.amount_per_payment) || null,
        frequency: cycle.frequency,
      });
      toast.success("Subscribe link created — copy it and send to the customer");
      setConfirming(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create GoCardless link");
    } finally {
      setBusy(null);
    }
  };

  const createCycle = async () => {
    if (!cycle.amount_per_payment || !cycle.start_date || !cycle.num_payments) {
      toast.error("Amount, start date and number of payments are required");
      return;
    }
    try {
      setBusy("cycle");
      const { data } = await api.post(`/gc/applications/${appId}/payment-cycle`, {
        amount_per_payment: parseFloat(cycle.amount_per_payment),
        frequency: cycle.frequency,
        start_date: cycle.start_date,
        num_payments: parseInt(cycle.num_payments),
      });
      toast.success(`Payment cycle created — ${data.subscriptions.length} subscription(s)`);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create payment cycle");
    } finally {
      setBusy(null);
    }
  };

  const cancelSetup = async () => {
    if (!window.confirm("Cancel this GoCardless setup? Any subscriptions will be cancelled too.")) return;
    try {
      setBusy("cancel");
      await api.post(`/gc/applications/${appId}/cancel`);
      toast.success("GoCardless setup cancelled");
      setStatus(null);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    } finally {
      setBusy(null);
    }
  };

  const live = status || gc;
  const methodLabel = method === "vrp" ? "VRP (Variable Recurring Payment)" : "Direct Debit (Bacs)";
  const isDaily = cycle.frequency === "daily";

  return (
    <div className="space-y-4" data-testid={`gc-panel-${method}`}>
      <Alert className="border-orange-300 bg-orange-50 py-2">
        <AlertTriangle className="h-4 w-4 text-orange-600" />
        <AlertTitle className="text-orange-800 font-bold text-sm flex items-center gap-1.5">
          NOT IN USE
          <InfoTip side="bottom">{HOW_TO[method]}</InfoTip>
        </AlertTitle>
        <AlertDescription className="text-orange-700 text-xs">
          Standing Order remains the live method — this {method.toUpperCase()} flow is built &amp; testable for the switch-over.
        </AlertDescription>
      </Alert>

      {!isThisMethod || live?.status === "cancelled" ? (
        <div className="p-4 border rounded-lg bg-white space-y-3">
          <p className="font-medium text-slate-800 flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Step 1 — Subscribe the customer
            <InfoTip>{HOW_TO[method]}</InfoTip>
          </p>
          {method === "vrp" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Payment amount (for VRP limits)</Label>
                <Input type="number" value={cycle.amount_per_payment}
                  onChange={(e) => setCycle({ ...cycle, amount_per_payment: e.target.value })}
                  data-testid={`gc-${method}-vrp-amount`} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Frequency</Label>
                <Select value={cycle.frequency} onValueChange={(v) => setCycle({ ...cycle, frequency: v })}>
                  <SelectTrigger data-testid={`gc-${method}-vrp-frequency`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {confirming ? (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid={`gc-${method}-confirm-box`}>
              <p className="text-sm font-medium text-blue-900">
                Create a {methodLabel} subscribe link for{" "}
                <span className="font-semibold">{application.company_name || appId}</span>?
              </p>
              <p className="text-xs text-blue-700">
                This creates a real GoCardless authorisation request. You&apos;ll get a link to send to the
                customer — nothing is collected until they authorise and a payment cycle is set.
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={createLink} disabled={busy === "create"}
                  data-testid={`gc-${method}-confirm-create-btn`}>
                  {busy === "create" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                  Yes, create link
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => setConfirming(true)} data-testid={`gc-${method}-create-link-btn`}>
              <Link2 className="w-4 h-4 mr-2" />
              Create Subscribe Link
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="p-3 bg-white border rounded-lg">
            <GCStepper status={live?.status} />
          </div>

          <div className="p-4 border rounded-lg bg-white space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-medium text-slate-800 flex items-center gap-2">
                <Link2 className="w-4 h-4" /> Step 1 — Customer subscription
                <InfoTip>{HOW_TO[method]}</InfoTip>
              </p>
              <span className={`text-xs px-2 py-1 rounded-full ${STATUS_LABELS[live?.status]?.cls || "bg-slate-100 text-slate-600"}`}
                data-testid={`gc-${method}-status-badge`}>
                {STATUS_LABELS[live?.status]?.label || live?.status}
              </span>
            </div>
            {live?.created_at && (
              <p className="text-[11px] text-muted-foreground">
                Invite created {new Date(live.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                {live.created_by && <> by {live.created_by}</>}
                {live.auth_url_expires_at && <> · link expires {new Date(live.auth_url_expires_at).toLocaleDateString("en-GB")}</>}
              </p>
            )}
            {live?.status === "link_created" && live?.auth_url && (
              <div className="flex items-center gap-2">
                <Input readOnly value={live.auth_url} className="text-xs font-mono flex-1" data-testid={`gc-${method}-auth-url`} />
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(live.auth_url);
                  toast.success("Link copied — send it to the customer");
                }} data-testid={`gc-${method}-copy-link-btn`}>
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            )}
            {live?.mandate_id && (
              <p className="text-sm text-emerald-700 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Mandate: <span className="font-mono">{live.mandate_id}</span>
                {live.mandate_status && <span className="text-xs text-muted-foreground">({live.mandate_status})</span>}
              </p>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => checkStatus(false)} disabled={busy === "status"}
                data-testid={`gc-${method}-check-status-btn`}>
                {busy === "status" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Check Status
              </Button>
              <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={cancelSetup}
                disabled={busy === "cancel"} data-testid={`gc-${method}-cancel-btn`}>
                {busy === "cancel" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <XCircle className="w-3 h-3 mr-1" />}
                Cancel Setup
              </Button>
            </div>
          </div>

          <div className={`p-4 border rounded-lg space-y-3 ${live?.mandate_id ? "bg-white" : "bg-slate-50 opacity-60"}`}>
            <p className="font-medium text-slate-800 flex items-center gap-2">
              <Banknote className="w-4 h-4" /> Step 2 — Payment cycle (from loan data)
              <InfoTip>Amount, frequency and term are pulled from the loan. Daily loans are created as 5 weekly GoCardless subscriptions (one per weekday) because GoCardless has no daily interval.</InfoTip>
            </p>
            {(live?.subscriptions?.length || 0) > 0 ? (
              <div className="space-y-1" data-testid={`gc-${method}-subs-list`}>
                {live.subscriptions.map((s) => (
                  <div key={s.id} className="flex justify-between text-sm bg-emerald-50 rounded px-3 py-1.5">
                    <span>{s.label} — from {s.start_date} {s.status && <span className="text-[10px] text-muted-foreground">({s.status})</span>}</span>
                    <span className="font-mono">£{Number(s.amount).toFixed(2)} × {s.count}</span>
                  </div>
                ))}
              </div>
            ) : live?.mandate_id ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Amount per payment (£)</Label>
                    <Input type="number" value={cycle.amount_per_payment}
                      onChange={(e) => setCycle({ ...cycle, amount_per_payment: e.target.value })}
                      data-testid={`gc-${method}-cycle-amount`} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Frequency</Label>
                    <Select value={cycle.frequency} onValueChange={(v) => setCycle({ ...cycle, frequency: v })}>
                      <SelectTrigger data-testid={`gc-${method}-cycle-frequency`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily (5× weekly subs, Mon–Fri)</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">First payment date</Label>
                    <Input type="date" value={cycle.start_date}
                      onChange={(e) => setCycle({ ...cycle, start_date: e.target.value })}
                      data-testid={`gc-${method}-cycle-start`} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Number of payments</Label>
                    <Input type="number" value={cycle.num_payments}
                      onChange={(e) => setCycle({ ...cycle, num_payments: e.target.value })}
                      data-testid={`gc-${method}-cycle-count`} />
                  </div>
                </div>
                <Button onClick={createCycle} disabled={busy === "cycle"} className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid={`gc-${method}-create-cycle-btn`}>
                  {busy === "cycle" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Create Payment Cycle
                  {isDaily && cycle.num_payments ? ` (5 weekly subs)` : ""}
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Unlocks once the customer has authorised the mandate.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
